//Built-in libraries

//Third party libraries
import { InformationCircleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import CheckboxGroup from '@/components/UI/Form/CheckboxGroup';
import InputError from '@/components/UI/Form/Error';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
//Services
import { formatNumber, isObjectEmpty } from '@/utils';

//STP Libraries

import { Accordion, AccordionItem } from '@/components/UI/Accordion';
import Modal from '@/components/UI/Modal';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import type { ReactSelectOption } from '@/types/Constants';
//Assets and Miscellaneous
import { States } from '../../../../../../../constants';
import { constructionTypes, initialValues, projectTypes } from './constant';
import type { IPrincipalCompletedProject } from './types';

//Component Begin

const PrincipalCompletedProjectForm = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editProjectId, setEditProjectId] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<IPrincipalCompletedProject>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showModal, setShowModal] = useState({
    addProject: false,
  });
  const [principalCompletedProject, setPrincipalCompletedProject] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setPrincipalCompletedProject(initialValues);
  };

  const editProjectHandler = (project: any) => {
    setIsEditing(true);
    const formData = Object.keys(principalCompletedProject).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...principalCompletedProject[next],
          value: project[next],
          isTouched: !!project[next],
        },
      };
    }, {});
    setPrincipalCompletedProject(formData);
    setEditProjectId(project.id);
    handleModal('addProject');
  };

  const updateProjectHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(principalCompletedProject).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: principalCompletedProject[next].value,
            id: editProjectId,
          };
        },
        {}
      );
      updateData({
        experience: {
          ...data.experience,
          principalCompleted: data.experience.principalCompleted.map((p: any) => {
            if (p.id === editProjectId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
      handleModal('addProject');
    }
  };

  const removeProjectHandler = (project: IPrincipalCompletedProject) => {
    setShowRemoveConfirm(true);
    setSelectedProject(project);
  };
  const confirmRemoveProject = async () => {
    setShowRemoveConfirm(false);
    const filteredProjects = data.experience.principalCompleted.filter((project: any) => project.id !== selectedProject?.id);
    updateData({
      experience: {
        ...data.experience,
        principalCompleted: filteredProjects,
      },
    });

    if (selectedProject?.id === editProjectId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveProject = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...principalCompletedProject };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setPrincipalCompletedProject((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddProjectHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(principalCompletedProject).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: principalCompletedProject[next].value,
          };
        },
        {}
      );
      updateData({
        experience: {
          ...data.experience,
          principalCompleted: [...data.experience.principalCompleted, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
      handleModal('addProject');
    }
  };

  const inputChangedHandler = createInputChangedHandler(setPrincipalCompletedProject, principalCompletedProject);

  const handleModal = (name: 'addProject') => {
    setShowModal((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Project'
          message={`You are about to remove ${selectedProject?.projectName} project. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveProject}
          decline={cancelRemoveProject}
        />
      )}
      <p className='block text-sm text-neutralColor-dark'>
        If the company does not have 3 projects to spotlight in the previous question, please detail the last 3 projects a key principal at
        the company has completed. Please also specify the name of that company at which the representative project was completed.
      </p>
      <div className='flex justify-end'>
        <Button label='Add Project' type='button' btnStyle='primary' onClick={() => handleModal('addProject')} />
      </div>
      {data.experience?.principalCompleted.length === 0 && (
        <div className='p-2 mt-2 flex flex-col items-center justify-center gap-2 h-screen/4'>
          <span>
            <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
          </span>
          <p>You have not added any principal completed projects</p>
        </div>
      )}
      {data.experience?.principalCompleted.length > 0 && (
        <Accordion multi>
          <div className='space-y-2'>
            {data.experience.principalCompleted.map((project: IPrincipalCompletedProject) => (
              <AccordionItem
                key={project.id}
                id={project.id}
                title={
                  <div key={project.id} className='flex justify-between items-center'>
                    <div>
                      <p className='font-bold'>{project.projectName}</p>
                      <span className='text-neutralColor-normal'>
                        {project.streetAddress}, {project.city}, {project.state.label}, {project.zipCode}
                      </span>
                    </div>
                    <div className='flex gap-4'>
                      <div className='cursor-pointer' onClick={() => editProjectHandler(project)}>
                        <PencilIcon className='h-5 w-5 text-neutralColor-normal hover:text-blue-500' />
                      </div>
                      <div className='cursor-pointer' onClick={() => removeProjectHandler(project)}>
                        <TrashIcon className='h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal' />
                      </div>
                    </div>
                  </div>
                }
              >
                <div className='border-t py-5 grid grid-cols-12 gap-5'>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Company name</span>
                    <span className='col-span-3'>{project.companyName}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Date Completed</span>
                    <span className='col-span-3'>{project.dateCompleted}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Gross Building Area</span>
                    <span className='col-span-3'>{formatNumber(project.grossBuildingArea ?? 0)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Number of Housing Units</span>
                    <span className='col-span-3'>{formatNumber(project.housingUnits ?? 0)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Total Development Cost</span>
                    <span className='col-span-3'>
                      <CurrencyFormatDisplay value={project.grossBuildingArea ?? 0} />
                    </span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Project Type</span>
                    <span className='col-span-3'>{project?.projectTypes?.map((p) => `${p}, `)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Construction Type</span>
                    <span className='col-span-3'>{project?.constructionTypes?.map((c) => `${c}, `)}</span>
                  </div>
                </div>
              </AccordionItem>
            ))}
          </div>
        </Accordion>
      )}
      {showModal.addProject && (
        <Modal customStyle='max-w-full'>
          <h2 className='m-0 mb-4'>Recent Principal Completed Project</h2>
          <div className='w-full grid grid-cols-6 gap-4'>
            <Input
              customClassName='col-span-6'
              label='Company Name'
              id='principalCompletedCompanyName'
              name='companyName'
              type='text'
              value={principalCompletedProject.companyName.value}
              error={principalCompletedProject?.companyName?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <Input
              customClassName='col-span-6 sm:col-span-4'
              label='Project Name'
              id='principalCompletedProjectName'
              name='projectName'
              type='text'
              value={principalCompletedProject.projectName.value}
              error={principalCompletedProject?.projectName?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='Date Completed (U&O)'
              id='principalCompletedDateCompleted'
              name='dateCompleted'
              type='date'
              value={principalCompletedProject.dateCompleted.value}
              error={principalCompletedProject?.dateCompleted?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <Input
              customClassName='col-span-6'
              label='Street Address'
              id='principalCompletedStreetAddress'
              name='streetAddress'
              type='text'
              value={principalCompletedProject.streetAddress.value}
              error={principalCompletedProject?.streetAddress?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='City'
              id='principalCompletedCity'
              name='city'
              type='text'
              value={principalCompletedProject.city.value}
              error={principalCompletedProject?.city?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
              <ReactSelect
                label='State / Province'
                id='principalCompletedState'
                name='state'
                options={States.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
                value={{
                  label: principalCompletedProject.state.value,
                  value: principalCompletedProject.state.value,
                }}
                error={principalCompletedProject?.state?.error}
                onChange={(option) => {
                  inputChangedHandler((option as ReactSelectOption).value, 'state');
                }}
                onBlur={(_e) => {
                  inputChangedHandler(principalCompletedProject.state.value, 'state');
                }}
              />
            </div>
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='ZIP / Postal code'
              id='principalCompletedZipCode'
              name='zipCode'
              type='number'
              value={principalCompletedProject.zipCode.value}
              error={principalCompletedProject?.zipCode?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />

            <div className='col-span-6 sm:col-span-2'>
              <label
                className={'leading-7 block text-sm font-medium text-neutralColor-medium'}
                htmlFor='principalCompletedGrossBuildingArea'
              >
                Gross Building Area
              </label>
              <NumberFormat
                id='principalCompletedGrossBuildingArea'
                name='grossBuildingArea'
                value={principalCompletedProject.grossBuildingArea.value ?? ''}
                displayType='input'
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'grossBuildingArea');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(principalCompletedProject.grossBuildingArea.value, e.target.name);
                }}
              />
              {principalCompletedProject?.grossBuildingArea?.error && (
                <InputError>{principalCompletedProject?.grossBuildingArea?.error}</InputError>
              )}
            </div>
            <div className='col-span-6 sm:col-span-2'>
              <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='principalCompletedHousingUnits'>
                Number of Housing units (if applicable)
              </label>
              <NumberFormat
                id='principalCompletedHousingUnits'
                name='housingUnits'
                value={principalCompletedProject.housingUnits.value ?? ''}
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'housingUnits');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(principalCompletedProject.housingUnits.value, e.target.name);
                }}
              />
              {principalCompletedProject?.housingUnits?.error && <InputError>{principalCompletedProject?.housingUnits?.error}</InputError>}
            </div>
            <div className='col-span-6 sm:col-span-2'>
              <label
                className={'leading-7 block text-sm font-medium text-neutralColor-medium'}
                htmlFor='principalCompletedTotalDevelopmentCost'
              >
                Total Development Cost
              </label>
              <NumberFormat
                name='developmentCost'
                id='principalCompletedTotalDevelopmentCost'
                value={principalCompletedProject.developmentCost.value ?? ''}
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                prefix={'$ '}
                maxLength={21}
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'developmentCost');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(principalCompletedProject.developmentCost.value, e.target.name);
                }}
              />
              {principalCompletedProject?.developmentCost?.error && (
                <InputError>{principalCompletedProject?.developmentCost?.error}</InputError>
              )}
            </div>

            <div className='col-span-6 md:col-span-3'>
              <CheckboxGroup
                label='Project Type (check all that apply):'
                id='principalCompletedProjectTypes'
                name='projectTypes'
                options={projectTypes.map((p) => ({
                  label: p.label,
                  value: p.label,
                }))}
                value={principalCompletedProject?.projectTypes.value || []}
                error={principalCompletedProject?.projectTypes?.error}
                onChange={(value) => inputChangedHandler(value, 'projectTypes')}
              />
            </div>
            <div className='col-span-6 md:col-span-3'>
              <CheckboxGroup
                label='Construction Type (check all that apply):'
                id='principalCompletedConstructionTypes'
                name='constructionTypes'
                options={constructionTypes.map((c) => ({
                  label: c.label,
                  value: c.label,
                }))}
                value={principalCompletedProject.constructionTypes.value || []}
                error={principalCompletedProject?.constructionTypes?.error}
                onChange={(value) => inputChangedHandler(value, 'constructionTypes')}
              />
            </div>
          </div>
          <div className='w-full flex justify-end gap-4 mt-10'>
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setIsEditing(false);
                handleModal('addProject');
              }}
            />
            <Button
              label={`${isEditing ? 'Save' : 'Add Project'}`}
              type='button'
              btnStyle='primary'
              onClick={isEditing ? updateProjectHandler : onAddProjectHandler}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default PrincipalCompletedProjectForm;
